import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { API } from "../constants";

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function NewsPreferencesPage() {
  const [catalog, setCatalog] = useState([]);
  const [selected, setSelected] = useState([]); // ordered list of source names
  const [showFavicons, setShowFavicons] = useState(true);
  const [tickerPosition, setTickerPosition] = useState(
    typeof window !== "undefined" ? localStorage.getItem("ts_ticker_position") || "top" : "top"
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const dragIndex = useRef(null);

  const changeTickerPosition = (pos) => {
    setTickerPosition(pos);
    localStorage.setItem("ts_ticker_position", pos);
    window.dispatchEvent(new CustomEvent("ts:ticker-position-change", { detail: pos }));
    toast.success(`News ticker moved to ${pos}`);
  };

  useEffect(() => {
    axios.get(`${API}/news/preferences`, { withCredentials: true, headers: authHeaders() })
      .then((res) => {
        setCatalog(res.data.catalog || []);
        setSelected(res.data.sources || []);
        if (typeof res.data.show_favicons === 'boolean') setShowFavicons(res.data.show_favicons);
      })
      .catch(() => toast.error("Failed to load news preferences"))
      .finally(() => setLoading(false));
  }, []);

  const toggleSource = (name) => {
    if (selected.includes(name)) {
      setSelected(selected.filter((s) => s !== name));
    } else {
      setSelected([...selected, name]);
    }
  };

  const onDragStart = (idx) => (e) => {
    dragIndex.current = idx;
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", String(idx)); } catch { /* some browsers throw */ }
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const onDrop = (idx) => (e) => {
    e.preventDefault();
    const from = dragIndex.current;
    if (from === null || from === idx) return;
    const next = [...selected];
    const [moved] = next.splice(from, 1);
    next.splice(idx, 0, moved);
    setSelected(next);
    dragIndex.current = null;
  };

  const moveUp = (idx) => {
    if (idx === 0) return;
    const next = [...selected];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    setSelected(next);
  };

  const moveDown = (idx) => {
    if (idx === selected.length - 1) return;
    const next = [...selected];
    [next[idx + 1], next[idx]] = [next[idx], next[idx + 1]];
    setSelected(next);
  };

  const save = async () => {
    if (selected.length === 0) {
      toast.error("Pick at least one news source");
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API}/news/preferences`,
        { sources: selected, show_favicons: showFavicons },
        { withCredentials: true, headers: authHeaders() }
      );
      toast.success("News preferences saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const available = catalog.filter((c) => !selected.includes(c.name));
  const grouped = available.reduce((acc, c) => {
    (acc[c.category] = acc[c.category] || []).push(c);
    return acc;
  }, {});

  return (
    <Layout>
      <div className="min-h-[calc(100vh-8rem)] py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="mb-10">
            <Link to="/dashboard" className="text-[#A3E635] text-sm hover:underline" data-testid="news-prefs-back">
              ← Back to Dashboard
            </Link>
            <h1 className="font-heading text-4xl font-bold text-white mt-3 mb-2" data-testid="news-prefs-title">
              News Preferences
            </h1>
            <p className="text-[#E8E4DC]">
              Choose which sources appear in your ticker and drag to reorder priority. Top sources show first.
            </p>
          </div>

          {/* Ticker position toggle */}
          <div className="card-glass p-5 mb-8" data-testid="news-prefs-position">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h3 className="font-heading text-white font-semibold mb-1">Ticker Position</h3>
                <p className="text-[#E8E4DC]/70 text-xs">Choose where headlines scroll across your screen.</p>
              </div>
              <div className="inline-flex rounded-full border border-white/15 overflow-hidden" role="radiogroup" aria-label="Ticker position">
                <button
                  type="button"
                  onClick={() => changeTickerPosition("top")}
                  className={`px-5 py-2 text-sm font-medium transition-colors ${tickerPosition === "top" ? "bg-[#A3E635] text-black" : "text-[#E8E4DC] hover:bg-white/5"}`}
                  data-testid="ticker-position-top"
                  aria-pressed={tickerPosition === "top"}
                >
                  ↑ Top
                </button>
                <button
                  type="button"
                  onClick={() => changeTickerPosition("bottom")}
                  className={`px-5 py-2 text-sm font-medium transition-colors ${tickerPosition === "bottom" ? "bg-[#A3E635] text-black" : "text-[#E8E4DC] hover:bg-white/5"}`}
                  data-testid="ticker-position-bottom"
                  aria-pressed={tickerPosition === "bottom"}
                >
                  ↓ Bottom
                </button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-[#E8E4DC] text-center py-12 card-glass" data-testid="news-prefs-loading">Loading…</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Selected (orderable) */}
              <div className="card-glass p-6" data-testid="news-prefs-selected">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading text-xl font-semibold text-white">Your Sources</h2>
                  <span className="text-xs text-[#A3E635]">{selected.length} selected</span>
                </div>
                {selected.length === 0 ? (
                  <p className="text-[#E8E4DC] text-sm py-8 text-center">
                    No sources picked yet. Add some from the catalog →
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {selected.map((name, idx) => {
                      const meta = catalog.find((c) => c.name === name);
                      return (
                        <li
                          key={name}
                          draggable
                          onDragStart={onDragStart(idx)}
                          onDragOver={onDragOver}
                          onDrop={onDrop(idx)}
                          className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10 hover:border-[#A3E635]/40 transition-colors cursor-move"
                          data-testid={`news-prefs-row-${name.replace(/\s+/g, '-').toLowerCase()}`}
                        >
                          <span className="text-[#E8E4DC]/60 select-none" title="Drag to reorder">⋮⋮</span>
                          <span className="text-xs text-[#A3E635] font-bold w-6">{idx + 1}</span>
                          {meta?.favicon && (
                            <img
                              src={meta.favicon}
                              alt=""
                              width="18"
                              height="18"
                              loading="lazy"
                              referrerPolicy="no-referrer"
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                              style={{ width: '18px', height: '18px', borderRadius: '3px', flexShrink: 0 }}
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm font-medium truncate">{name}</p>
                            <p className="text-[#E8E4DC]/60 text-xs">{meta?.category}</p>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <button onClick={() => moveUp(idx)} disabled={idx === 0} className="text-[#E8E4DC] hover:text-[#A3E635] disabled:opacity-30 p-1" aria-label="Move up" data-testid={`news-prefs-up-${idx}`}>
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M5 15l7-7 7 7" /></svg>
                            </button>
                            <button onClick={() => moveDown(idx)} disabled={idx === selected.length - 1} className="text-[#E8E4DC] hover:text-[#A3E635] disabled:opacity-30 p-1" aria-label="Move down" data-testid={`news-prefs-down-${idx}`}>
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M19 9l-7 7-7-7" /></svg>
                            </button>
                            <button onClick={() => toggleSource(name)} className="text-[#E8E4DC] hover:text-red-400 p-1" aria-label="Remove" data-testid={`news-prefs-remove-${idx}`}>
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}

                <div className="mt-6 mb-4 p-3 rounded-lg bg-white/5 border border-white/10 flex items-center justify-between" data-testid="news-prefs-favicon-toggle">
                  <div className="min-w-0 pr-3">
                    <p className="text-white text-sm font-medium">Show source favicons</p>
                    <p className="text-[#E8E4DC]/60 text-xs">Tiny logo next to each headline in the ticker</p>
                  </div>
                  <button
                    onClick={() => setShowFavicons(v => !v)}
                    role="switch"
                    aria-checked={showFavicons}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 ${showFavicons ? 'bg-[#A3E635]' : 'bg-white/20'}`}
                    data-testid="news-prefs-favicon-switch"
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${showFavicons ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>

                <button
                  onClick={save}
                  disabled={saving}
                  className="btn-primary w-full disabled:opacity-50"
                  data-testid="news-prefs-save"
                >
                  {saving ? "Saving…" : "Save Preferences"}
                </button>
              </div>

              {/* Catalog */}
              <div className="card-glass p-6" data-testid="news-prefs-catalog">
                <h2 className="font-heading text-xl font-semibold text-white mb-4">Add Sources</h2>
                {Object.keys(grouped).length === 0 ? (
                  <p className="text-[#E8E4DC] text-sm py-8 text-center">All sources added.</p>
                ) : (
                  Object.entries(grouped).map(([category, items]) => (
                    <div key={category} className="mb-5 last:mb-0">
                      <p className="text-[#A3E635] text-xs uppercase tracking-wider font-semibold mb-2">{category}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {items.map((c) => (
                          <button
                            key={c.name}
                            onClick={() => toggleSource(c.name)}
                            className="flex items-center justify-between gap-2 p-2.5 rounded-lg bg-white/5 border border-white/10 hover:border-[#A3E635]/40 hover:bg-[#A3E635]/5 transition-all text-left"
                            data-testid={`news-prefs-add-${c.name.replace(/\s+/g, '-').toLowerCase()}`}
                          >
                            <span className="flex items-center gap-2 min-w-0">
                              {c.favicon && (
                                <img
                                  src={c.favicon}
                                  alt=""
                                  width="16"
                                  height="16"
                                  loading="lazy"
                                  referrerPolicy="no-referrer"
                                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                  style={{ width: '16px', height: '16px', borderRadius: '3px', flexShrink: 0 }}
                                />
                              )}
                              <span className="text-white text-sm truncate">{c.name}</span>
                            </span>
                            <span className="text-[#A3E635] text-lg font-bold leading-none">+</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
